import { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, TableShell, Loading, EmptyState } from "@/components/common";
import UploadDialog from "@/components/modals/UploadDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { gbp, formatDate } from "@/lib/format";
import { Plus, UploadCloud, Search, Pencil, Trash2, Download } from "lucide-react";

const BLANK = { category: "", basis: "Full Pallet", location: "", unit: "", y1_price: 0, y2_price: 0 };

export default function RateCardTable() {
  const qc = useQueryClient();
  const { data = [], isLoading } = useQuery({ queryKey: ["rate-cards"], queryFn: () => api.get("/rate-cards").then((r) => r.data) });

  const [search, setSearch] = useState("");
  const [depot, setDepot] = useState("all");
  const [category, setCategory] = useState("all");
  const [basisFilter, setBasisFilter] = useState("all");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [editing, setEditing] = useState(null); // object or null
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const depots = useMemo(() => [...new Set(data.map((d) => d.location).filter(Boolean))].sort(), [data]);
  const categories = useMemo(() => [...new Set(data.map((d) => d.category).filter(Boolean))].sort(), [data]);
  const bases = useMemo(() => [...new Set(data.map((d) => d.basis).filter(Boolean))].sort(), [data]);

  const filtered = useMemo(() => {
    return data.filter((r) => {
      if (depot !== "all" && r.location !== depot) return false;
      if (category !== "all" && r.category !== category) return false;
      if (basisFilter !== "all" && r.basis !== basisFilter) return false;
      if (search) {
        const s = search.toLowerCase();
        if (!`${r.category} ${r.location} ${r.unit}`.toLowerCase().includes(s)) return false;
      }
      return true;
    });
  }, [data, depot, category, basisFilter, search]);

  const openAdd = () => { setEditing(null); setForm(BLANK); };
  const openEdit = (r) => { setEditing(r); setForm({ category: r.category, basis: r.basis || "", location: r.location, unit: r.unit, y1_price: r.y1_price, y2_price: r.y2_price }); };

  const save = async () => {
    if (!form.category.trim()) { toast.error("Category is required"); return; }
    setSaving(true);
    try {
      const payload = { ...form, y1_price: Number(form.y1_price) || 0, y2_price: Number(form.y2_price) || 0 };
      if (editing) await api.put(`/rate-cards/${editing.id}`, payload);
      else await api.post("/rate-cards", payload);
      toast.success(editing ? "Rate card updated" : "Rate card added");
      qc.invalidateQueries({ queryKey: ["rate-cards"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
      setForm(BLANK);
      setDialogClosed();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const [dialogOpen, setDialogOpen] = useState(false);
  const setDialogClosed = () => setDialogOpen(false);
  const handleOpenAdd = () => { openAdd(); setDialogOpen(true); };
  const handleOpenEdit = (r) => { openEdit(r); setDialogOpen(true); };

  const confirmDelete = async () => {
    try {
      await api.delete(`/rate-cards/${deleteTarget.id}`);
      toast.success("Rate card deleted");
      qc.invalidateQueries({ queryKey: ["rate-cards"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setDeleteTarget(null);
    }
  };

  const exportCsv = () => {
    const header = ["category", "basis", "location", "unit", "y1_price", "y2_price"];
    const rows = filtered.map((r) => header.map((h) => `"${r[h] ?? ""}"`).join(","));
    const csv = [header.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "epgroup-rate-cards.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <PageHeader title="Rate Cards" subtitle="Maintain depot delivery, storage and movement rates. Upload to keep them up to date.">
        <Button variant="outline" onClick={exportCsv} data-testid="rate-card-export-button"><Download className="w-4 h-4 mr-1.5" /> Export</Button>
        <Button variant="outline" onClick={() => setUploadOpen(true)} data-testid="rate-card-upload-csv-button"><UploadCloud className="w-4 h-4 mr-1.5" /> Upload</Button>
        <Button onClick={handleOpenAdd} className="bg-[#d4333b] hover:bg-[#b82a31]" data-testid="rate-card-add-button"><Plus className="w-4 h-4 mr-1.5" /> Add rate</Button>
      </PageHeader>

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Search category, depot or unit…" className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="rate-card-search-input" />
        </div>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="w-full sm:w-52" data-testid="rate-card-category-filter"><SelectValue placeholder="Category" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={basisFilter} onValueChange={setBasisFilter}>
          <SelectTrigger className="w-full sm:w-40" data-testid="rate-card-basis-filter"><SelectValue placeholder="Basis" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All bases</SelectItem>
            {bases.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={depot} onValueChange={setDepot}>
          <SelectTrigger className="w-full sm:w-44" data-testid="rate-card-depot-filter"><SelectValue placeholder="Depot" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All depots</SelectItem>
            {depots.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {isLoading ? <Loading /> : (
        <TableShell>
          <table className="w-full text-sm" data-testid="rate-card-table">
            <thead>
              <tr className="bg-slate-100 dark:bg-slate-800/80 text-left text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-400">
                <th className="px-4 py-3">Rate Category</th>
                <th className="px-4 py-3">Basis</th>
                <th className="px-4 py-3">Depot / Location</th>
                <th className="px-4 py-3">Unit</th>
                <th className="px-4 py-3 text-right">Y1 Price</th>
                <th className="px-4 py-3 text-right">Y2 Price</th>
                <th className="px-4 py-3">Last Updated</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {filtered.map((r) => (
                <tr key={r.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/40" data-testid={`rate-row-${r.id}`}>
                  <td className="px-4 py-3 font-medium text-slate-800 dark:text-slate-200">{r.category}</td>
                  <td className="px-4 py-3">{r.basis ? <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${r.basis === "Case" ? "bg-violet-50 text-violet-700" : "bg-blue-50 text-blue-700"}`}>{r.basis}</span> : <span className="text-slate-400">—</span>}</td>
                  <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{r.location || "—"}</td>
                  <td className="px-4 py-3 text-slate-500">{r.unit || "—"}</td>
                  <td className="px-4 py-3 text-right font-mono-num">{gbp(r.y1_price)}</td>
                  <td className="px-4 py-3 text-right font-mono-num">{gbp(r.y2_price)}</td>
                  <td className="px-4 py-3 text-slate-500 font-mono-num">{formatDate(r.updated_at)}</td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1">
                      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => handleOpenEdit(r)} data-testid={`rate-edit-${r.id}`}><Pencil className="w-4 h-4" /></Button>
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-red-500 hover:text-red-600" onClick={() => setDeleteTarget(r)} data-testid={`rate-delete-${r.id}`}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length === 0 && <EmptyState message="No rate cards match your filters." />}
        </TableShell>
      )}

      {/* Add / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="rate-card-form-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit rate card" : "Add rate card"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <Label>Rate category *</Label>
              <Input className="mt-1.5" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="e.g. Delivery" data-testid="form-category" />
            </div>
            <div>
              <Label>Depot / Location</Label>
              <Input className="mt-1.5" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="e.g. Greenside" data-testid="form-location" />
            </div>
            <div>
              <Label>Rate basis</Label>
              <Select value={form.basis} onValueChange={(v) => setForm({ ...form, basis: v })}>
                <SelectTrigger className="mt-1.5" data-testid="form-basis"><SelectValue placeholder="Select basis" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Full Pallet">Full Pallet</SelectItem>
                  <SelectItem value="Case">Case</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Unit</Label>
              <Input className="mt-1.5" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} placeholder="e.g. per pallet" data-testid="form-unit" />
            </div>
            <div>
              <Label>Y1 Price (£)</Label>
              <Input className="mt-1.5" type="number" step="0.01" value={form.y1_price} onChange={(e) => setForm({ ...form, y1_price: e.target.value })} data-testid="form-y1" />
            </div>
            <div>
              <Label>Y2 Price (£)</Label>
              <Input className="mt-1.5" type="number" step="0.01" value={form.y2_price} onChange={(e) => setForm({ ...form, y2_price: e.target.value })} data-testid="form-y2" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-[#d4333b] hover:bg-[#b82a31]" data-testid="rate-card-save-button">{saving ? "Saving…" : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <UploadDialog
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        entity="rate-cards"
        columns={["category", "basis", "location", "unit", "y1_price", "y2_price"]}
        onDone={() => { qc.invalidateQueries({ queryKey: ["rate-cards"] }); qc.invalidateQueries({ queryKey: ["stats"] }); }}
      />

      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete rate card?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove <span className="font-medium">{deleteTarget?.category} {deleteTarget?.location}</span>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-button">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
